import { ReactElement, MouseEvent, useMemo, useCallback } from 'react';
import {
  Button,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';

import {
  useToggleMenu,
  useSubscriptionFeatures,
  useAppSelector,
} from '~hooks/index';
import { useAccessFeatureModalContext } from '~contexts/AccessFeatureModalContext';
import { NewFeatureBagde, AccessFeature } from '~components/index';
import { AccessFeatureCommonProps } from '~components/AccessFeature/types';
import { selectAuth } from '~store/slices/auth';
import { isAdminUser } from '~utils/user';

import { ActionOrderMenuProps, ActionOrderMenuOptionItem } from './types';
import ActionsOrderMenuIconWithLoading from './IconWithLoading';
import { ActionOrderMenuContainerMenu } from './styles';

const ActionOrderMenu = ({
  options,
  buttonProps,
}: ActionOrderMenuProps): ReactElement => {
  const [anchorElMenu, openMenu, , handleToggleMenu] = useToggleMenu();

  const actions = useMemo<ActionOrderMenuOptionItem[]>(
    () => options.filter((item) => item.enabled),
    [options],
  );

  const { handleOpen: handleOpenAccesFeatureModal } =
    useAccessFeatureModalContext();
  const { hasAccessToFeature } = useSubscriptionFeatures();
  const { user } = useAppSelector(selectAuth);

  const hasValidFeature = useCallback(
    (validSubscriptionFeature: AccessFeatureCommonProps) =>
      hasAccessToFeature(
        validSubscriptionFeature.validFeature,
        validSubscriptionFeature.availableToDate,
      ),
    [hasAccessToFeature],
  );

  const isAdminCurrentUser = useMemo(() => isAdminUser(user), [user]);

  const handleClickItem = useCallback(
    (option: ActionOrderMenuOptionItem) =>
      async (event: MouseEvent<HTMLLIElement>) => {
        event.preventDefault();
        const validSubscriptionFeature = option.validSubscriptionFeature;

        if (validSubscriptionFeature) {
          const isValiFeature = hasValidFeature(validSubscriptionFeature);

          if (!isValiFeature) {
            if (isAdminCurrentUser) {
              handleToggleMenu(false);

              handleOpenAccesFeatureModal({
                validFeature: validSubscriptionFeature.validFeature,
                validateAvailableRoutesState: false,
              });
            }

            return;
          }
        }

        await option.onClick();

        const closeOnClick = option.closeOnClick ?? true;

        if (closeOnClick) handleToggleMenu(false);
      },
    [
      handleToggleMenu,
      hasValidFeature,
      isAdminCurrentUser,
      handleOpenAccesFeatureModal,
    ],
  );

  const getDisabledItem = useCallback(
    (option: ActionOrderMenuOptionItem) => {
      const defaultDisabled =
        Boolean(option.disabled) || Boolean(option.loading);

      const validSubscriptionFeature = option.validSubscriptionFeature;

      if (validSubscriptionFeature && !isAdminCurrentUser) {
        return defaultDisabled || !hasValidFeature(validSubscriptionFeature);
      }

      return defaultDisabled;
    },
    [hasValidFeature, isAdminCurrentUser],
  );

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<MoreIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        ref={anchorElMenu}
        aria-controls={openMenu ? 'actions-orders-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openMenu ? 'true' : undefined}
        onClick={() => handleToggleMenu(true)}
        {...buttonProps}
      >
        {buttonProps?.label ?? 'Acciones'}
      </Button>

      <ActionOrderMenuContainerMenu
        id="actions-orders-menu"
        anchorEl={anchorElMenu.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={openMenu}
        onClose={() => handleToggleMenu(false)}
        elevation={0}
      >
        {actions.map((actionItem, actionIndex) => (
          <div key={`menu-actions-order-item-${actionItem.label}`}>
            <MenuItem
              onClick={handleClickItem(actionItem)}
              disabled={getDisabledItem(actionItem)}
            >
              <ListItemIcon>
                <ActionsOrderMenuIconWithLoading
                  loading={Boolean(actionItem.loading)}
                >
                  {actionItem.Icon}
                </ActionsOrderMenuIconWithLoading>
              </ListItemIcon>

              <ListItemText primary={actionItem.label} />

              {actionItem.newActiveForVersion && (
                <NewFeatureBagde
                  activeForVersion={actionItem.newActiveForVersion}
                />
              )}

              {actionItem.validSubscriptionFeature && (
                <AccessFeature.NextPlanChip
                  validFeature={
                    actionItem.validSubscriptionFeature.validFeature
                  }
                  availableToDate={
                    actionItem.validSubscriptionFeature.availableToDate
                  }
                />
              )}
            </MenuItem>

            {actionIndex < actions.length - 1 && <Divider />}
          </div>
        ))}
      </ActionOrderMenuContainerMenu>
    </>
  );
};

export default ActionOrderMenu;
